import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kSettleOnce, kSettleEither } from "../../../Admin/kReusableCoreTS/kSettleOnce";
import { AppHistoryAnswer, AppHistoryDone, AppSendHistory } from "./AppSendHistory";
import { AppSendExtra, AppSendState, AppSource, AppTextDone, AppTextFail } from "./AppSource";
import { AppSendVerdict, AppSignUpApi, SignUpAnswer } from "./AppSignUpApi";
import { AppSignUpPost } from "./AppSignUpPost";
import { AppAccountPost } from "./AppAccountPost";
import { DropboxReadRules } from "./DropboxReadRules";
import { AppMediaRules } from "./AppMediaRules";
import { AppMediaDownload } from "./AppMediaDownload";
import { AppOneDriveRules } from "./AppOneDrive";
import { AppOneDriveFetch } from "./AppOneDriveRun";

// AppDropboxSource - the folder in the subscriber's own Dropbox, read by their own browser.
//
// ==================================================================================================
// !! ⭐ THIS IS THE PIECE THAT WAS MISSING, AND ITS ABSENCE WAS A DEAD BUTTON
// ==================================================================================================
//
// ⛔ MEASURED 2026-09-23: the service wrote 55 files into `Apps/WACommander` and the page's only
// remote reader was `AppDriveSource`, so the archive was WRITTEN to Dropbox and READ from Google.
// Both halves reported success; the subscriber pressed "Open WACommander" and nothing happened.
// ⚠ It is also what pins `Drive*` in place: deleting the Bridge's Drive code while the app still
// read Drive would have left nothing anywhere, with every dashboard green.
//
// ==================================================================================================
// !! ⭐ IT READS FROM DROPBOX AND SENDS THROUGH `/send` - NEVER BY WRITING INTO DROPBOX
// ==================================================================================================
//
// The Dropbox ticket carries `files.content.read` and deliberately not `files.content.write`
// (`FolderTicketRules` has the argument), and that is unchanged. ⛔ UNTIL 2026-09-24 `Queue` REFUSED
// EVERY SEND for that reason - honest, and the product's most important act had no door (owner:
// "Sending WhatsApp messages is crucial - make sure that works first").
//
// ⭐ NOW `Queue` POSTS ONE REQUEST TO `/send`, which proves the archive key, checks its send scope,
// stamps the approver from the PROVED key, runs the sender's own `Outbox.Admits`, and drops the
// request into the folder the 20-second scan reads. `SendRules` has the whole argument, including
// what this does to the key (it can now send as well as read) and the bounds on that.
//
// ⚠ `Check` IS WHAT LETS THE PAGE SAY "SENT". The receipt is written by the sender on the same disk,
// so the page can ask for it within seconds - instead of the old "appears after the next folder
// rebuild", which was a quarter of an hour of not knowing.
//
// ==================================================================================================
// !! ⚠ EVERY READ IS ONE REQUEST, WHICH IS WHY THERE IS NO ID CACHE HERE
// ==================================================================================================
//
// `AppDriveSource` caches ids because Drive has no paths and every read is a walk. Dropbox takes the
// whole path, so there is nothing to cache and no cache to go stale. ⭐ `DropboxSyncRun` deleted
// `State.Ids` on the writing side for the identical reason: **here the path IS the identity.**
//
// ⚠ WHY NOT `kSPRest.RestExecute` (law 2, and the guard asks every time this file is touched). The
// answer is `AppSource`'s header, unchanged and for the same reasons: it is the SharePoint transport
// - it queues into `kSPRest._RestWaitingLine`, resolves a web url, attaches `X-RequestDigest` and
// branches on `ks.KApplication.apptype`. There is no SharePoint web, no digest and no page context
// here; this is a static page sending a bearer header to `content.dropboxapi.com`. ⛔ AND IT WOULD
// BREAK THIS CALL SPECIFICALLY: the argument travels in `Dropbox-API-Arg` with NO request body and
// no `Content-Type`, which is not a shape that transport can express.

export class AppDropboxSource extends AppSource {
    public override CanQueryMessages = (): boolean => {
        return this.ServiceUrl !== "" && this.Number !== "" && this.Key !== "";
    };

    public override QueryMessages = (Query: Record<string, unknown>, Done: AppTextDone): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Body: Record<string, unknown> = Object.assign({ action: "list" }, Query, { number: this.Number, key: this.Key });
            function Answer(Status: number, Text: string): void { Done(Text); }
            new AppAccountPost(AppSignUpApi.UrlFor(this.ServiceUrl, "/messages"), JSON.stringify(Body), Answer).Send();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppDropboxSource.QueryMessages"); }
    };
    /**
     * The composer's answer when this page holds no key to send with.
     *
     * ⚠ IT NAMES BOTH WAYS IN, SIGN IN FIRST, IN THE BUTTONS' OWN WORDS (`AppAccountViews.WaysIn`).
     * Somebody composing a message has signed up already - on this device or another - so "open
     * WACommander again from your sign-up" pointed at a flow they cannot re-enter: their live
     * number refuses a second claim. Plain text: it is shown inline under the composer.
     */
    public static readonly NotSignedInToSend: string = "this page is not signed in to send. Sign in"
        + " on this device to reconnect it, or sign up if you have not yet.";

    /** The sent-messages list's answer when this page holds no key - see `NotSignedInToSend`. */
    public static readonly NotSignedInToList: string = "this page is not signed in, so your sent"
        + " messages cannot be listed. Sign in on this device to reconnect it, or sign up if you"
        + " have not yet.";

    private Token: string;
    private Root: string;
    /** Where `/send` is, and the proof it takes. "" until `SendAs` - and then `Queue` refuses. */
    private ServiceUrl: string = "";
    private Number: string = "";
    private Key: string = "";
    /**
     * ⭐ WAVE 3 (DORMANT): the archive lives in OneDrive (`/folder` answered `storage: "onedrive"`, no token). Every read
     * then asks `/onedrive` for that one file's pre-authenticated address (`AppOneDriveFetch`) - the key is the proof.
     */
    private OneDrive: boolean = false;

    /** The archive is a OneDrive one (see `OneDrive`). DOCUMENTED EXCEPTION: sets a flag. */
    public UseOneDrive = (): void => {
        this.OneDrive = true;
    };

    /**
     * `Root` is the app folder's root as the service named it - "" - and NEVER "/".
     *
     * ⚠ DROPBOX REFUSES `"/"` AS A PATH while accepting `"/name"`, so a root of `"/"` would make
     * every single read fail with a malformed-path error rather than with anything that names the
     * cause. `DropboxApi.PathFor` already carries that rule on the service side; `/folder` sends ""
     * so the two cannot disagree.
     */
    public constructor(Token: string, Root: string) {
        super();
        this.Token = nz(Token);
        this.Root = nz(Root);
    }

    /**
     * DOCUMENTED EXCEPTION: returns a literal.
     * ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw.
     */
    public Describe = (): string => {
        return "your Dropbox";
    };

    /**
     * Give this source the means to send: the service, and the archive key that proves the page.
     *
     * ⚠ SEPARATE FROM THE CONSTRUCTOR because the read ticket and the send proof arrive from
     * different places - the ticket from `/folder`'s answer, the key from `AppArchiveStore`.
     */
    public SendAs = (ServiceUrl: string, Number: string, Key: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.ServiceUrl = nz(ServiceUrl);
            this.Number = nz(Number);
            this.Key = nz(Key);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxSource.SendAs");
        }
    };

    /**
     * ⚠ A PLACEHOLDER THE SERVICE OVERWRITES, AND THAT IS DELIBERATE. `/send` stamps `approvedBy`
     * from the PROVED key (`SendRules.ApproverFor`), because a page that could name its own approver
     * could name anybody. This only has to be non-empty so `AppOutbox.Build` lets the press through.
     *
     * DOCUMENTED EXCEPTION: returns a literal.
     * ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw.
     */
    public Who = (): string => {
        return "this browser";
    };

    /** DOCUMENTED EXCEPTION: returns a literal. ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw. */
    public CanCheck = (): boolean => {
        return true;
    };

    /**
     * ⛔ NO "BRIDGE", NO `npm`. The old line named an internal component and a command a subscriber
     * cannot run, and it was false here: nothing on their computer needs to be running.
     *
     * DOCUMENTED EXCEPTION: returns a literal.
     * ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw.
     */
    public SendNote = (): string => {
        // ⛔ NOT A FORECAST (audit 2026-10-12: it said "usually within half a minute"): half a minute is how often the
        // service sends what is waiting (`BridgeLiveOptions.OutboxSeconds`, 20 s), held to it by `SignUpPromise.test.ts`.
        return "Sent from your own WhatsApp number, within half a minute.";
    };

    public LoadIndex = (OnDone: AppTextDone, OnFail: AppTextFail): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.LoadPage(DropboxReadRules.IndexName, OnDone, OnFail);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxSource.LoadIndex");
        }
    };

    public LoadPage = (Href: string, OnDone: AppTextDone, OnFail: AppTextFail): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Path: string = DropboxReadRules.PathFor(this.Root, Href);
            if (Path.length === 0) {
                OnFail("That link does not point at anything in your WACommander folder.");
                return;
            }
            this.Get(Path, OnDone, OnFail);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxSource.LoadPage");
        }
    };

    /**
     * A person file beside a page - the AI history (`ai-chat.json`) - with the SAME read ticket.
     * ⭐ "NOT THERE" IS ITS OWN ANSWER (`OnMissing`): Dropbox says it with a 409 `path/not_found`
     * (`DropboxReadRules.Missing`), and for a contact nobody has asked about yet that is the normal case.
     */
    public LoadBeside = (Href: string, OnDone: AppTextDone, OnMissing: () => void, OnFail: AppTextFail): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Path: string = DropboxReadRules.PathFor(this.Root, Href);
            if (Path.length === 0) { OnFail("That file is not in your WACommander folder."); return; }
            const Req: XMLHttpRequest = new XMLHttpRequest();
            const Reader: AppDropboxRead = new AppDropboxRead(Req, OnDone, OnFail);
            Reader.Missing = OnMissing;
            this.GetVia(Path, Req, Reader);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxSource.LoadBeside");
            OnFail(AppDropboxRead.NotAsked);
        }
    };

    /**
     * One photo or voice note, read with the READ ticket and handed back as a `blob:` URL.
     *
     * ⭐ THE SAME DOWNLOAD AS A PAGE, WITH THE SAME TICKET - `files.content.read` and nothing else.
     * Nothing about media widens what this page can do (`FolderTicketRules`).
     *
     * ⚠ AN `arraybuffer`, NOT TEXT: `responseText` would decode the JPEG as UTF-8 and corrupt it. The
     * blob is labelled with the type from the extension (`AppMediaRules.TypeFor`), because Dropbox
     * answers every download as `application/octet-stream` and an `<audio>` is less forgiving of
     * that than an `<img>`.
     */
    public LoadMedia = (Href: string, OnDone: AppTextDone, OnFail: AppTextFail): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Path: string = DropboxReadRules.PathFor(this.Root, Href);
            if (Path.length === 0) { OnFail("That photo is not in your WACommander folder."); return; }
            const Req: XMLHttpRequest = new XMLHttpRequest();
            const Reader: AppDropboxMedia = new AppDropboxMedia(Req, AppMediaRules.TypeFor(Href), OnDone, OnFail);
            this.GetVia(Path, Req, Reader, "arraybuffer", "AppDropboxSource.LoadMedia");
        } catch (e) {
            // ⚠ THE `GetVia` RULE: a request that cannot even be built is a defect (the arg is
            // escaped to ASCII, so no header should ever be refused) and is REPORTED - and the
            // filler is still answered, or it would wait on this photo for ever.
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxSource.LoadMedia");
            OnFail(AppDropboxRead.NotAsked);
        }
    };

    public DownloadMedia = (ChatId: string, MessageId: string, OnDone: AppTextDone, OnFail: AppTextFail): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.ServiceUrl.length === 0 || this.Number.length === 0 || this.Key.length === 0) { OnFail("Sign in to download this media."); return; }
            new AppMediaDownload(this.ServiceUrl.replace(/\/$/, "") + "/media/download",
                JSON.stringify({ number: this.Number, key: this.Key, action: "download", chatId: ChatId, messageId: MessageId }), OnDone, OnFail).Start();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppDropboxSource.DownloadMedia"); }
    };

    /**
     * Hand one message to `/send`. `OnDone` gets the id the service accepted.
     *
     * ⚠ `Name` IS UNUSED HERE - it is the FILE name the other sources write, and the service names
     * the file itself from the request's id, which it validates. A page-chosen file name reaching a
     * server's disk is exactly what `SendRules.IdOk` exists to prevent.
     */
    public Queue = (Name: string, Json: string, OnDone: AppTextDone, OnFail: AppTextFail, OnUnknown: AppTextDone | null = null): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Body: string = AppSignUpApi.SendBody(this.Number, this.Key, Json);
            const Url: string = AppSignUpApi.UrlFor(this.ServiceUrl, AppSignUpApi.Send);
            if (Body.length === 0 || Url.length === 0) {
                OnFail(AppDropboxSource.NotSignedInToSend);
                return;
            }
            const Reader: AppSendReply = new AppSendReply(OnDone, OnFail, Name.substring(0, Name.length - 5), OnUnknown);
            new AppSignUpPost(Url, Body, Reader.OnQueued, 45000, Reader.OnUnknown).Send();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxSource.Queue");
        }
    };

    /** DOCUMENTED EXCEPTION: returns a literal. ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw. */
    public CanHistory = (): boolean => {
        return true;
    };

    /**
     * Ask the service which messages have been sent - newest first, receipts and waiting requests.
     *
     * ⚠ ITS OWN TRANSPORT, NOT `AppSignUpPost`: that one reads every answer into `SignUpAnswer`,
     * which has no list, so the rows would be dropped on the floor. The MEANING of the answer is
     * `AppSendHistory.Parse`'s, tested without a browser and pinned against the service.
     */
    public History = (OnDone: AppHistoryDone): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Body: string = AppSendHistory.Body(this.Number, this.Key);
            const Url: string = AppSignUpApi.UrlFor(this.ServiceUrl, AppSignUpApi.Send);
            if (Body.length === 0 || Url.length === 0) {
                OnDone(AppSendHistory.No(AppDropboxSource.NotSignedInToList));
                return;
            }
            const Reader: AppHistoryReply = new AppHistoryReply(OnDone);
            new AppAccountPost(Url, Body, Reader.OnAnswer).Send();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxSource.History");
        }
    };

    /** Ask the service how message `Id` has fared. */
    public Check = (Id: string, OnState: AppSendState): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Body: string = AppSignUpApi.CheckBody(this.Number, this.Key, Id);
            const Url: string = AppSignUpApi.UrlFor(this.ServiceUrl, AppSignUpApi.Send);
            if (Body.length === 0 || Url.length === 0) { OnState(false, true, "", false); return; }
            const Reader: AppCheckReply = new AppCheckReply(OnState);
            new AppSignUpPost(Url, Body, Reader.OnAnswer, 15000).Send();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxSource.Check");
        }
    };

    /**
     * One download.
     *
     * ⚠ THE ARGUMENT IS A HEADER AND THE BODY IS EMPTY - the shape `files/upload` proved by refusing
     * `application/json` (measured 2026-09-23). ⛔ `Content-Type` IS NOT SET: the Dropbox content
     * endpoints reject a JSON content type on these calls, and setting one here would fail every
     * read with a message about the type rather than about the file.
     */
    private Get = (Path: string, OnDone: AppTextDone, OnFail: AppTextFail): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Req: XMLHttpRequest = new XMLHttpRequest();
            this.GetVia(Path, Req, new AppDropboxRead(Req, OnDone, OnFail));
        } catch (e) {
            // Reached only when the request or its reader could not even be built - `GetVia`
            // answers every later failure through the reader's latch - so nothing has answered yet.
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxSource.Get");
            OnFail(AppDropboxRead.NotAsked);
        }
    };

    /**
     * ⛔ A FAILURE HERE MUST STILL ANSWER, AND ONCE. The catch used to report and stop, so a
     * `setRequestHeader` or `open` that threw left the page saying "Reading your Dropbox" for ever
     * - with no sentence and nothing to press. It answers through the reader's latch now, which is
     * also what stops a late `readystatechange` answering a second time.
     */
    private GetVia = (Path: string, Req: XMLHttpRequest, Reader: IDropboxReader,
                      ResponseType: XMLHttpRequestResponseType = "", Label: string = "AppDropboxSource.GetVia"): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.OneDrive === true) {
                new AppOneDriveFetch(AppSignUpApi.UrlFor(this.ServiceUrl, AppOneDriveRules.Route),
                    AppOneDriveRules.FileBody(this.Number, this.Key, Path), Reader).Start();
                return;
            }
            Req.onreadystatechange = Reader.OnState;
            Req.open("POST", DropboxReadRules.DownloadUrl(), true);
            if (ResponseType.length > 0) { Req.responseType = ResponseType; }
            Req.setRequestHeader("Authorization", "Bearer " + this.Token);
            Req.setRequestHeader("Dropbox-API-Arg", DropboxReadRules.DownloadArg(Path));
            Req.send();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, Label);
            Reader.Failed(AppDropboxRead.NotAsked);
        }
    };
}

/**
 * Reads one download response.
 *
 * ⚠ A NAMED CLASS RATHER THAN A CLOSURE because the house style forbids anonymous functions, and
 * `onreadystatechange` needs something that can see the request it belongs to.
 */
class AppDropboxRead {
    /** What the page says when the request could not even be made. */
    public static readonly NotAsked: string = "That page could not be requested from your Dropbox."
        + " Reload WACommander to try again.";

    private Req: XMLHttpRequest;
    private Done: AppTextDone;
    private Fail: AppTextFail;
    private Result: kSettleOnce<AppDropboxAnswer>;
    /** When set, a 409 `path/not_found` is answered here rather than as a failure (`LoadBeside`). */
    public Missing: (() => void) | null = null;

    public constructor(Req: XMLHttpRequest, Done: AppTextDone, Fail: AppTextFail) {
        this.Result = new kSettleOnce<AppDropboxAnswer>(this.Deliver);
        this.Req = Req;
        this.Done = Done;
        this.Fail = Fail;
    }

    /**
     * ⛔ EVERY FAILURE HERE IS FATE 1, AND THAT IS NOT LAZINESS. An expired ticket, a dropped
     * connection and a page that has not been rebuilt yet are all things a correct deployment
     * reaches doing a normal thing. Reporting them would flip `KErrorMode` and blank the archive
     * the subscriber is reading - the rule the deleted `AppDriveSource.Get` carried, and the same one
     * `DossierFolderRun.Refuse` was measured getting wrong in production.
     */
    public OnState = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the caller is waiting on this callback
        // and a silent return leaves the page saying "Reading your Dropbox" for ever.
        try {
            if (this.Req.readyState !== 4) { return; }
            if (this.Req.status >= 200 && this.Req.status < 300) {
                this.Read(this.Req.responseText);
                return;
            }
            if (this.Missing !== null && DropboxReadRules.Missing(this.Req.status, nz(this.Req.responseText)) === true) {
                this.NotThere();
                return;
            }
            this.Failed(DropboxReadRules.Why(this.Req.status, nz(this.Req.responseText)));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxRead.OnState");
        }
    };

    /** ⛔ One of the two answers - `Read` or `Failed` - and only the first ever lands. */
    public Read = (Text: string): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - see OnState; the latch is what keeps
        // this to once, and a throwing callback is reported rather than answered again.
        try {
            const Out: AppDropboxAnswer = new AppDropboxAnswer();
            Out.Kind = "read"; Out.Text = Text;
            this.Result.Settle(Out);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxRead.Read");
        }
    };

    /** A OneDrive read's bytes: the same answer as a Dropbox read of the text. */
    public Bytes = (Data: Uint8Array): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - see Read; the latch keeps it to once.
        try { this.Read(new TextDecoder("utf-8").decode(Data)); } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppDropboxRead.Bytes"); }
    };

    /** "Not there", under the same latch as `Read`. */
    public NotThere = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - see Read.
        try {
            // ⚠ A reader with no `Missing` (a page, the index) still answers: OneDrive's "not there" reaches it here.
            if (this.Missing === null) { this.Failed("That file is not in your folder yet (path/not_found)."); return; }
            const Out: AppDropboxAnswer = new AppDropboxAnswer();
            Out.Kind = "missing";
            this.Result.Settle(Out);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxRead.NotThere");
        }
    };

    /** ⛔ The failing answer, under the same latch as `Read`. */
    public Failed = (Why: string): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - see Read.
        try {
            const Out: AppDropboxAnswer = new AppDropboxAnswer();
            Out.Kind = "failed"; Out.Text = Why;
            this.Result.Settle(Out);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxRead.Failed");
        }
    };
    /** ERROR-SUPPRESSED-JUSTIFIED: named ungated adapter; each settlement caller catches callback faults. */
    private Deliver = (Out: AppDropboxAnswer): void => {
        if (Out.Kind === "read") { this.Done(Out.Text); return; }
        if (Out.Kind === "missing") { if (this.Missing !== null) { this.Missing(); } return; }
        this.Fail(Out.Text);
    };

}

/**
 * Reads one media download into a `blob:` URL, answered ONCE.
 *
 * ⚠ `responseText` IS NEVER TOUCHED: with `responseType = "arraybuffer"` a browser THROWS on it, so
 * a refusal is described from the status alone - which loses `path/not_found`'s wording and keeps
 * the part that matters (401 = the ticket expired, 0 = the connection dropped).
 */
export class AppDropboxMedia {
    private Req: XMLHttpRequest;
    private Type: string;
    private Result: kSettleEither<string, string>;

    public constructor(Req: XMLHttpRequest, Type: string, Done: AppTextDone, Fail: AppTextFail) {
        this.Req = Req;
        this.Type = Type;
        this.Result = new kSettleEither<string, string>(Done, Fail);
        this.Result.Rearm();
    }

    /** ⛔ FATE 1 THROUGHOUT - see `AppDropboxRead.OnState`: a missing photo must not blank the page. */
    public OnState = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the frame's filler is waiting on this and
        // moves to the next photo only once it is answered.
        try {
            if (this.Req.readyState !== 4 || this.Result.IsPending() === false) { return; }
            if (this.Req.status >= 200 && this.Req.status < 300 && this.Req.response !== null) {
                const Made: Blob = new Blob([this.Req.response as ArrayBuffer], { type: this.Type });
                this.Result.Succeed(URL.createObjectURL(Made));
                return;
            }
            this.Result.Fail(DropboxReadRules.Why(this.Req.status, ""));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDropboxMedia.OnState");
        }
    };
    /** ERROR-SUPPRESSED-JUSTIFIED: failed request construction must answer the waiting filler once. */
    public Failed = (Why: string): void => {
        try { this.Result.Fail(Why); }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppDropboxMedia.Failed"); }
    };
    /** A OneDrive read's bytes -> the same `blob:` URL a Dropbox read makes. */
    public Bytes = (Data: Uint8Array): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the frame's filler waits on this answer.
        try {
            if (this.Result.IsPending() === false) { return; }
            this.Result.Succeed(URL.createObjectURL(new Blob([Data as BlobPart], { type: this.Type })));
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppDropboxMedia.Bytes"); }
    };

    /** A photo that is not there is a failed photo (the filler keeps the words) - fate 1. */
    public NotThere = (): void => {
        this.Failed("That file is not in your folder (path/not_found).");
    };

}

/** Reads `/send`'s answer to a send. */
class AppSendReply {
    private Done: AppTextDone;
    private Fail: AppTextFail;
    private Id: string;
    private Unknown: AppTextDone | null;

    public constructor(Done: AppTextDone, Fail: AppTextFail, Id: string, Unknown: AppTextDone | null) {
        this.Done = Done;
        this.Fail = Fail;
        this.Id = Id;
        this.Unknown = Unknown;
    }

    /** ERROR-SUPPRESSED-JUSTIFIED: complete the page's waiting ticket; never resubmit an uncertain POST. */
    public OnUnknown = (): void => {
        try {
            if (this.Unknown !== null) { this.Unknown(this.Id); return; }
            this.Done(this.Id);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSendReply.OnUnknown");
        }
    };

    /**
     * ⛔ ONLY `queued: true` IS SUCCESS. Any other answer - a refusal, a lost connection, a reply
     * this build cannot read - is shown as the service's own sentence and the text STAYS IN THE BOX:
     * retyping a message the product lost is the thing that would stop the owner trusting it at all.
     */
    public OnQueued = (Answer: SignUpAnswer): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the page is waiting on this answer, and
        // a silent return leaves the Send button disabled for ever.
        try {
            if (Answer.Ok === true && Answer.Queued === true && Answer.Id.length > 0) {
                this.Done(Answer.Id);
                return;
            }
            this.Fail(nz(Answer.Why));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSendReply.OnQueued");
        }
    };
}

/**
 * One POST of a history ask, answered ONCE with `AppSendHistory.Parse`'s reading of the body.
 *
 * ⚠ THE `AppSignUpPost` SHAPE (body read whatever the status, one latched answer), with the raw text
 * handed to the history parser instead of `SignUpAnswer`, which has no field for a list.
 */
export class AppHistoryReply {
    private Result: kSettleOnce<AppHistoryAnswer>;

    public constructor(Done: AppHistoryDone) {
        this.Result = new kSettleOnce<AppHistoryAnswer>(Done);
    }

    /** ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the page is waiting on this answer. */
    public OnAnswer = (Status: number, Text: string): void => {
        try {
            this.Finish(AppSendHistory.Parse(Status, Text, AppSignUpApi.OursWhy()));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppHistoryReply.OnAnswer");
        }
    };

    /** The ONLY place `Done` is called. Latched before delivering. */
    private Finish = (Answer: AppHistoryAnswer): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the one thing that releases the page.
        try {
            this.Result.Settle(Answer);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppHistoryReply.Finish");
        }
    };
}

/** Reads `/send`'s answer to "how did it go?". */
class AppCheckReply {
    private OnState: AppSendState;

    public constructor(OnState: AppSendState) {
        this.OnState = OnState;
    }

    /**
     * ⚠ THE MEANING IS `AppSignUpApi.SendVerdictOf`'s, NOT DECIDED HERE, so the rules - lost
     * connection keeps waiting, unconfirmed is final - are tested without a browser and pinned
     * against the service by `SendContract.test.ts`. This only delivers them.
     */
    public OnAnswer = (Answer: SignUpAnswer): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the page's poll is waiting on this.
        try {
            const Verdict: AppSendVerdict = AppSignUpApi.SendVerdictOf(Answer);
            const Extra: AppSendExtra = new AppSendExtra();
            Extra.SentText = Verdict.SentText;
            Extra.TranslateFailed = Verdict.TranslateFailed;
            this.OnState(Verdict.Sent, Verdict.Final, Verdict.Why, Verdict.Unconfirmed, Extra);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppCheckReply.OnAnswer");
        }
    };
}

interface IDropboxReader {
    OnState: () => void;
    Failed: (Why: string) => void;
    /** "Not there" (a OneDrive read's `missing: true`; Dropbox says it with a 409 the reader reads itself). */
    NotThere: () => void;
    /** A OneDrive read: the file's bytes as the service read them. */
    Bytes: (Data: Uint8Array) => void;
}

/** One of the three outside download outcomes. */
class AppDropboxAnswer {
    public Kind: string = "";
    public Text: string = "";
}
